/**
 * IntlInvoicesPage — Invoices internacionales (partner licenses).
 *
 * Flujo: los PDFs llegan por email o se suben manualmente, el parser LLM
 * extrae la información, el usuario revisa/edita y contabiliza.
 */
import { tenantLocale } from '@/lib/locale'
import { useState } from 'react'

import { ClipboardList, Download, Globe2, RefreshCw, Upload, AlertTriangle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { FilterBar, useFilters, type FilterConfig } from '@/components/filters'
import { TableSkeleton } from '@/components/Skeleton'
import { ConfigBadge, type BadgeConfig } from '@/components/Badge'
import { useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import apiClient from '@/lib/apiClient'

import {
  useIntlInvoices,
  useUploadIntlInvoice,
} from '../hooks/useIntlInvoices'
import type { IntlInvoiceStatus } from '../types'
import { IntlInvoiceDetailModal } from './IntlInvoiceDetailModal'

const STATUS_CONFIG: BadgeConfig<IntlInvoiceStatus> = {
  received: { label: 'Recibida', variant: 'slate' },
  parsed: { label: 'Parseada', variant: 'blue' },
  ready: { label: 'Lista', variant: 'indigo' },
  posted: { label: 'Contabilizada', variant: 'emerald' },
  error: { label: 'Error', variant: 'red' },
  discarded: { label: 'Descartada', variant: 'slate' },
}

const FILTER_CONFIG: FilterConfig = [
  {
    type: 'search',
    key: 'search',
    placeholder: 'Nº invoice, email o asunto…',
    placement: 'quick',
  },
  {
    type: 'select',
    key: 'status',
    label: 'Estado',
    placement: 'quick',
    options: [
      { value: '', label: 'Todos' },
      { value: 'received,parsed,ready,error', label: 'Pendientes' },
      { value: 'received', label: 'Recibida' },
      { value: 'parsed', label: 'Parseada' },
      { value: 'ready', label: 'Lista' },
      { value: 'error', label: 'Error' },
      { value: 'posted', label: 'Contabilizada' },
    ],
  },
  {
    type: 'date-range',
    key: 'date',
    label: 'Fecha',
    placement: 'quick',
    defaultPreset: 'current-month',
  },
]


function fmtNumber(value: string | number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || value === '') return '--'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '--'
  try {
    return new Intl.NumberFormat(tenantLocale(), {
      style: 'currency', currency, maximumFractionDigits: currency === 'CLP' ? 0 : 2,
    }).format(num)
  } catch {
    return `${num.toFixed(2)} ${currency}`
  }
}


export default function IntlInvoicesPage() {
  const filters = useFilters(FILTER_CONFIG)
  // useFilters para date-range genera date_from / date_to en params
  const { data, isLoading, isError, refetch } = useIntlInvoices(filters.params as Record<string, string>)
  const upload = useUploadIntlInvoice()
  const toast = useToast()
  const { can } = usePermission()
  const canCreate = can('intl_invoices', 'create')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const invoices = data ?? []

  const handleExport = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams(filters.params as Record<string, string>)
      const res = await apiClient.get(`/intl-invoices/export/?${params.toString()}`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      const cd = (res.headers['content-disposition'] as string) || ''
      const match = /filename="?([^"]+)"?/.exec(cd)
      a.download = match ? match[1] : 'invoices_internacionales.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Error al exportar.')
    } finally {
      setExporting(false)
    }
  }

  const handleUploadClick = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/pdf,.pdf'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const created = await upload.mutateAsync({ file })
        toast.success('PDF subido. El parser extraerá los datos en segundos.')
        setSelectedId(created.id)
      } catch (exc) {
        toast.error('Error al subir el PDF.')
        console.error(exc)
      }
    }
    input.click()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <FilterBar config={FILTER_CONFIG} filters={filters} />
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            leftIcon={<RefreshCw size={14} />}
          >
            Refrescar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExport}
            isPending={exporting}
            disabled={invoices.length === 0}
            leftIcon={<Download size={14} />}
          >
            Exportar
          </Button>
          {canCreate && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleUploadClick}
              isPending={upload.isPending}
              leftIcon={<Upload size={14} />}
            >
              Subir PDF
            </Button>
          )}
        </div>
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : isError ? (
          <EmptyState
            icon={ClipboardList}
            title="Error al cargar invoices"
            hint="Reintenta en unos segundos."
          />
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={Globe2}
            title="No hay invoices internacionales"
            hint={
              filters.hasActiveFilters
                ? 'Prueba con otros filtros.'
                : 'Sube un PDF o reenvía invoices a tu casilla de expense.'
            }
          />
        ) : (
          <DataTable
            columns={[
              {
                key: 'supplier_name', header: 'Partner',
                render: inv => inv.supplier_name ?? <span className="text-warning-content-soft text-xs">(sin asignar)</span>,
              },
              {
                key: 'external_number', header: 'Nº invoice',
                render: inv => inv.external_number
                  ? <span className="font-mono">{inv.external_number}</span>
                  : <span className="text-content-subtle">—</span>,
              },
              {
                key: 'invoice_date', header: 'Fecha',
                render: inv => inv.invoice_date ?? <span className="text-content-subtle">—</span>,
              },
              {
                key: 'amount_total_original', header: 'Total orig.', numeric: true,
                render: inv => fmtNumber(inv.amount_total_original, inv.currency || 'USD'),
              },
              {
                key: 'amount_total_clp', header: 'Total CLP', numeric: true,
                render: inv => inv.status === 'posted' ? fmtNumber(inv.amount_total_clp, 'CLP') : '—',
              },
              {
                key: 'status', header: 'Estado',
                render: inv => (
                  <div className="flex flex-col items-start gap-1">
                    <ConfigBadge value={inv.status} config={STATUS_CONFIG} />
                    {inv.is_possible_duplicate && inv.status !== 'posted' && inv.status !== 'discarded' && (
                      <span
                        title="Ya existe un documento contabilizado con este proveedor y número. Revísalo antes de contabilizar."
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-danger-surface text-danger-content"
                      >
                        <AlertTriangle size={10} />Posible duplicado
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: 'source', header: 'Origen',
                render: inv => (
                  <span className="flex items-center gap-1.5">
                    {inv.source === 'email' ? <Download size={13} /> : <Upload size={13} />}
                    <span className="capitalize">{inv.source === 'email' ? 'Email' : 'Upload'}</span>
                  </span>
                ),
              },
            ]}
            rows={invoices}
            rowKey={inv => inv.id}
            onRowClick={inv => setSelectedId(inv.id)}
            // El panel ya lo pone el contenedor, que envuelve también el
            // esqueleto de carga y los dos estados vacíos.
            surface={false}
            minWidth={900}
            caption="Invoices internacionales"
          />
        )}
      </div>

      {selectedId && (
        <IntlInvoiceDetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  )
}
